import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ALERT_SEVERITY_ORDER } from '@detect/shared';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { dashboardService } from '../services/dashboards';
import { LoadingSpinner, ErrorState } from '../components/common';

/**
 * Overview / NOC landing: live open-alert pulse + tenant-wide resource
 * status grid, both riding the widget-data contracts (one source of
 * truth for dashboard AND overview math).
 */

const REFRESH_MS = 30_000;

interface AlertListData {
  alerts: { id: string; severity: string; resourceName: string }[];
}

interface NocData {
  resources: { id: string; name: string; worstSeverity: string | null }[];
}

export function Home() {
  const { tokenReady } = useAuth();
  const { tenant, tenantId } = useTenant();

  const alertsQuery = useQuery<AlertListData>({
    queryKey: ['tenant', tenantId, 'overview-alerts'],
    enabled: tokenReady && tenantId !== null,
    refetchInterval: REFRESH_MS,
    queryFn: () =>
      dashboardService.widgetData(tenantId as string, {
        type: 'alert_list',
        config: { minSeverity: 'warning', statuses: ['open'], limit: 100 },
      }),
  });
  const nocQuery = useQuery<NocData>({
    queryKey: ['tenant', tenantId, 'overview-noc'],
    enabled: tokenReady && tenantId !== null,
    refetchInterval: REFRESH_MS,
    queryFn: () =>
      dashboardService.widgetData(tenantId as string, {
        type: 'noc_status',
        config: {},
      }),
  });

  const counts = { critical: 0, error: 0, warning: 0 };
  for (const a of alertsQuery.data?.alerts ?? []) {
    if (a.severity in counts) counts[a.severity as keyof typeof counts] += 1;
  }
  const capped = (alertsQuery.data?.alerts.length ?? 0) >= 100;

  return (
    <div className="page">
      <h1 className="page-title">Overview{tenant ? ` — ${tenant.name}` : ''}</h1>

      {alertsQuery.isError && (
        <ErrorState
          error={alertsQuery.error}
          onRetry={() => void alertsQuery.refetch()}
        />
      )}
      <div className="overview-cards">
        {[...(['critical', 'error', 'warning'] as const)]
          .sort((a, b) => ALERT_SEVERITY_ORDER[b] - ALERT_SEVERITY_ORDER[a])
          .map((severity) => (
            <Link
              key={severity}
              to="/alerts"
              className={`overview-card overview-${severity}`}
            >
              <div className="overview-count">
                {/* isPending covers DISABLED (pre-bootstrap) queries too —
                    never render a fake 0 before data exists. */}
                {alertsQuery.isPending || alertsQuery.isError
                  ? '…'
                  : `${counts[severity]}${capped ? '+' : ''}`}
              </div>
              <div className="overview-label">{severity} open</div>
            </Link>
          ))}
      </div>

      <h2>Resource status</h2>
      {nocQuery.isPending ? (
        <LoadingSpinner />
      ) : nocQuery.isError ? (
        <ErrorState error={nocQuery.error} onRetry={() => void nocQuery.refetch()} />
      ) : (nocQuery.data?.resources.length ?? 0) === 0 ? (
        <p className="text-muted">
          No resources yet — <Link to="/resources">add one</Link> or set up{' '}
          <Link to="/discovery">discovery</Link>.
        </p>
      ) : (
        <div className="noc-grid">
          {nocQuery.data?.resources.map((r) => (
            <Link
              key={r.id}
              to={`/resources/${r.id}`}
              className={`noc-cell noc-${r.worstSeverity ?? 'ok'}`}
              title={r.name}
            >
              {r.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
